import {
  cloneRgObject,
  dtoToRgBrief,
  RgDataSetID,
  type World as RgWorld,
} from "@project-rouge/rg-core";
import {
  Brief2PipeProps,
  RgPipes,
  RunPipeline,
  type RunPipelineReturn,
} from "@project-rouge/rg-aggregator";
import { CreateBoxWorld } from "src/utils/CreateBoxWorld";
import { CreatePolyWorld } from "src/utils/CreatePolyWorld";
import { getDaiwaTypology, type DaiwaTypologyId } from "src/screens/daiwa-typologies";

// Drives the Tessellation Steps screen. Runs the aggregator pipeline
// incrementally (Brief2PipeProps once, then RunPipeline advancing `from` after
// each stage so heavy stages like NMCS aren't repeated), capturing a cloned
// snapshot of the world after every grouped stage.

export type BriefInputs = {
  datasetId: RgDataSetID;
  length: number;
  width: number;
  floors: number;
  /** when set, the footprint comes from a Daiwa typology and length/width are ignored */
  typology?: DaiwaTypologyId | null;
};

// Per-dataset starter dimensions. These are the values the brief snaps to
// when you pick a dataset; they're meant to be a known-good starting point,
// not a constraint — tune them in the UI from there.
//
// uk_01: 35×35×5 — the cost-tracking "Medium · Mid" variant, proven.
// uk_02: 38.25×30×5 — length is 10 × gridCellWidth (3.825m) so modules align
//                     cleanly on uk_02's grid; depth fits two module rows
//                     (moduleDepth 7.175) + a 2m corridor with margin.
//                     Adjust here if a better default emerges.
export const DATASET_DEFAULTS: Record<RgDataSetID, BriefInputs> = {
  [RgDataSetID.uk_01]: {
    datasetId: RgDataSetID.uk_01,
    length: 35,
    width: 35,
    floors: 5,
  },
  [RgDataSetID.uk_02]: {
    datasetId: RgDataSetID.uk_02,
    length: 38.25,
    width: 30,
    floors: 5,
  },
};

export const DEFAULT_BRIEF: BriefInputs = DATASET_DEFAULTS[RgDataSetID.uk_01];

export type StageDef = {
  id: string;
  label: string;
  /** the RgPipes value to run up to (inclusive) */
  to: RgPipes;
  /** human-readable algorithm name */
  algo: string;
  /** one or two sentences sourced from the TESSA manual */
  description: string;
  /** highlights for the side panel — what to look at on this step */
  notes?: string[];
};

// Grouped pipeline stages — 7 meaningful steps mapping to RgPipes targets.
// The narrative matches the manual (subdivision → skeleton → unit aggregation).
export const STAGES: StageDef[] = [
  {
    id: "subdivision",
    label: "Subdivision",
    to: RgPipes.AddPlots,
    algo: "Oriented Bounding Box Binary Space Partitioning (OBB-BSP)",
    description:
      "The zone polygon is recursively split into smaller plots along its smallest bounding box. Splitting is gated by isValidSubdivision (minWidth, maxLength, minArea, maxCount, splitLikelihood).",
    notes: [
      "Plots are the containers each building will grow inside.",
      "When both splitting and not-splitting are valid, an RNG picks via splitLikelihood (0.5 by default).",
    ],
  },
  {
    id: "skeleton",
    label: "Skeleton growth",
    to: RgPipes.SkeletonTailorAnatomy,
    algo: "Skeleton Tailor (thread growth + look-ahead)",
    description:
      "A 'needle' (point + direction) grows into 'threads' inside each plot. At each potential turn the tailor forecasts up to ~640 options (40 back-offsets × 2 ends × left/right turns) and picks the one that produces the longest skeleton.",
    notes: [
      "Threads are constrained by the plot boundary and by neighbouring plots' threads.",
      "meta.mainThreads is drawn as polylines on this step.",
    ],
  },
  {
    id: "muscles",
    label: "Muscles (corridor strips)",
    to: RgPipes.AddMuscles,
    algo: "Anatomy build — joints, bones, muscles",
    description:
      "The skeleton hardens into a graph: joints (corners), bones (segments), and muscles — the strips along each bone side where apartments and cores will sit.",
    notes: ["Drawn as the lines along each bone."],
  },
  {
    id: "cores",
    label: "Cores & entrances",
    to: RgPipes.CalcAnatomyEntrances,
    algo: "Core placement rules (aggConfig.coreWith…)",
    description:
      "Cores (stair / lift / fire-fighting variants) and entrances are placed based on the dataset's aggConfig rules: counts depend on building height (FFL) and area (GIA).",
    notes: [
      "uk_01 rules require 2 stair cores for FFL ≥ 30m; 1 lift core for ≥4 floors; etc.",
      "Cores are drawn as filled rectangles on the muscles.",
    ],
  },
  {
    id: "nmcs",
    label: "Unit aggregation — apartments",
    to: RgPipes.AggNmcs,
    algo: "Nested Monte Carlo Search (NMCS)",
    description:
      "Apartments are picked from the dataset's catalogue and placed along each muscle by treating the problem as a sequence of actions. Random rollouts of valid actions are scored, and the best terminal-state action is chosen at each step.",
    notes: [
      "This is the actual tessellation: modules tile onto the muscle grid.",
      "Apartment choice respects placement flags (nextToEntrance, aboveEntrance, closeCorridor).",
    ],
  },
  {
    id: "corridors",
    label: "Corridors, corner cores, levels",
    to: RgPipes.SetLevelData,
    algo: "Geometric fill + level data",
    description:
      "Corridors are drawn between muscles, corner cores are added at joints, and per-level data (FFL, GIA polygons) is computed.",
    notes: ["Last step before the envelope."],
  },
  {
    id: "envelope",
    label: "Envelope — facade & roof",
    to: RgPipes.SetBuildingIndexes,
    algo: "Roof / parapet / facade / openings",
    description:
      "Roof, parapets, facade, glazing and openings are generated around the building, then polygons are finalised and buildings indexed. The result is the same World the cost engine consumes.",
  },
];

export type StageSnapshot = {
  stage: StageDef;
  /** cloned world at this stage (safe to render — won't mutate further) */
  obj: RgWorld;
  meta: RunPipelineReturn["meta"];
};

/**
 * Runs the pipeline incrementally and snapshots after each grouped stage.
 * Single forward pass — `from` advances after each pipe so earlier stages
 * (including NMCS) aren't repeated.
 */
export function runStages(brief: BriefInputs): StageSnapshot[] {
  const world = brief.typology
    ? CreatePolyWorld(
        getDaiwaTypology(brief.typology).polygon,
        brief.floors,
        brief.datasetId
      )
    : CreateBoxWorld(brief.length, brief.width, brief.floors, brief.datasetId);
  const rgBrief = dtoToRgBrief(world.rgBriefDto);
  const pipeProps = Brief2PipeProps({ brief: rgBrief, start: 0, count: 1 })[0];
  const aggProps = pipeProps.aggProps;
  const obj = pipeProps.obj as RgWorld;

  const snapshots: StageSnapshot[] = [];
  let from = RgPipes.Filter;
  for (const stage of STAGES) {
    const { meta } = RunPipeline({
      obj,
      aggProps,
      briefIndex: 0,
      from,
      to: stage.to,
    });
    snapshots.push({
      stage,
      obj: cloneRgObject(obj) as RgWorld,
      meta,
    });
    from = (stage.to + 1) as RgPipes;
  }
  return snapshots;
}
